// ─── Average restock cycles by household size ─────────────────────────────────

const AVG_CYCLES: Record<number, Record<string, number>> = {
  1: { milk: 9, eggs: 12, bread: 8 },
  2: { milk: 6, eggs: 8,  bread: 5 },
  3: { milk: 4, eggs: 6,  bread: 4 },
  4: { milk: 3, eggs: 5,  bread: 3 },
}

const HOUSEHOLD_SIZE_MAP: Record<string, number> = {
  just_me:        1,
  me_and_partner: 2,
  family:         4,
}

// ─── findTip ──────────────────────────────────────────────────────────────────

export function findTip(
  pantryItems: { name: string; emoji: string; avg_restock_cycle_days: number | null; confirmation_count: number }[],
  householdSize: string | number,
): { text: string; emoji: string } | null {
  const numericSize = typeof householdSize === 'string'
    ? (HOUSEHOLD_SIZE_MAP[householdSize] ?? 2)
    : householdSize

  const avgs = AVG_CYCLES[Math.min(numericSize, 4)] ?? AVG_CYCLES[2]

  for (const item of pantryItems) {
    if (!item.avg_restock_cycle_days || item.confirmation_count < 4) continue

    const name = item.name.toLowerCase()
    let avg: number | null = null

    if (name.includes('milk'))      avg = avgs.milk
    else if (name.includes('egg'))  avg = avgs.eggs
    else if (name.includes('bread')) avg = avgs.bread

    if (!avg) continue

    const cycle = item.avg_restock_cycle_days
    const diff  = (cycle - avg) / avg

    if (diff > 0.3) {
      return {
        emoji: item.emoji || '💡',
        text:  `You buy ${item.name} every ${Math.round(cycle)} days. Most ${numericSize}-person households buy it every ${avg} days. Try buying less at a time.`,
      }
    }

    if (diff < -0.3) {
      return {
        emoji: item.emoji || '💡',
        text:  `You go through ${item.name} faster than most. Try buying 2 at a time to reduce trips.`,
      }
    }
  }

  return null
}

// ─── OptimizerTip ─────────────────────────────────────────────────────────────

interface Props {
  tip:       { text: string; emoji: string }
  onDismiss: () => void
}

export function OptimizerTip({ tip, onDismiss }: Props) {
  return (
    <div className="bg-[#FDF6E3] border border-[#8B6914]/20 rounded-2xl p-4 mb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1">
          <span className="text-base mt-0.5">💡</span>
          <p className="text-sm text-[#7A7468] leading-relaxed">{tip.text}</p>
        </div>
        <button
          onClick={onDismiss}
          className="text-[#B0A99F] text-lg leading-none shrink-0 mt-0.5"
          aria-label="Dismiss tip"
        >
          ×
        </button>
      </div>
    </div>
  )
}
